import {TokenManager} from "@/service/auth/TokenManager";
import {API_ENDPOINTS} from "@/global/api/config/api-endpoints";
import {defaultApiClient} from "@/global/api/default-api";
import {refreshTokenApiClient} from "@/global/api/refresh-token-api";
import type { LoginParams, PasswordResetParams, EmailVerificationParams, AuthResponse } from '@/types/auth.types'
import {ConstService} from "@/service/ConstService";
import router from "@/router";

export class AuthService {

    private static readonly tokenManager = new TokenManager();
    private static readonly BASE_URL = API_ENDPOINTS.BASE;

    public static async login(loginParams: LoginParams) {
        AuthService.clearAuthData();

        const response = await defaultApiClient({
            ...API_ENDPOINTS.AUTH.LOGIN
            , data: loginParams,
            withCredentials: true
        });

        AuthService.updateToken(response?.body.data.accessToken);
        AuthService.updateUser(response?.body.data.user);
        // AuthService.setTokenCookies(response);
    }

    public static async logout() {
        AuthService.clearAuthData();
        await router.push({ name: 'login' });
        // TODO
        // const response = await defaultApiClient(API_ENDPOINTS.AUTH.LOGOUT);
    }

    public static async passwordResetRequest(params: PasswordResetParams): Promise<AuthResponse> {
        try {
            const response = await defaultApiClient.post(`${this.BASE_URL}/password-reset-request`, params);
            return {
                success: true,
                body: response.data
            };
        } catch (error: any) {
            return {
                success: false,
                ...error.response?.data
            };
        }
    }

    public static async passwordReset(params: PasswordResetParams): Promise<AuthResponse> {
        try {
            const response = await defaultApiClient.post(`${this.BASE_URL}/password-reset`, params);
            return {
                success: true,
                body: response.data.body.data
            };
        } catch (error: any) {
            return {
                success: false,
                ...error.response?.data
            };
        }
    }

    public static async validPasswordReset(params: PasswordResetParams): Promise<AuthResponse> {
        try {
            const response = await defaultApiClient.post(`${this.BASE_URL}/password-reset`, params);
            return {
                success: true,
                body: response.data.body.data
            };
        } catch (error: any) {
            return {
                success: false,
                ...error.response?.data
            };
        }
    }

    // TODO 문서 어디에 있지?????
    public static async confirmEmailRequest(params: EmailVerificationParams): Promise<AuthResponse> {
        try {
            const response = await defaultApiClient.post(`${this.BASE_URL}/email-verification-request`, params);
            return {
                success: true,
                body: response.data
            };
        } catch (error: any) {
            return {
                success: false,
                ...error.response?.data
            };
        }
    }

    // TODO 문서 어디에 있지?????
    public static async confirmEmail(params: EmailVerificationParams): Promise<AuthResponse> {
        try {
            const response = await defaultApiClient.post(`${this.BASE_URL}/email-verification`, params);
            return {
                success: true,
                body: response.data
            };
        } catch (error: any) {
            return {
                success: false,
                ...error.response?.data
            };
        }
    }

    public static async refreshAccessToken(): Promise<string> {
        const response = await defaultApiClient({
            ...API_ENDPOINTS.AUTH.RENEW
            , withCredentials: true
        });
        console.log('refresh access response : ', response);
        const newToken = response.data.accessToken;
        const newUser = response.data.user;

        AuthService.updateToken(newToken);
        AuthService.updateUser(newUser);

        return newToken;
    }

    public static async handleLogout(router: any): Promise<void> {
        await AuthService.logout();
        const currentPath = router.currentRoute.value.fullPath;
        if (currentPath !== API_ENDPOINTS.AUTH.LOGIN.url) {
            localStorage.setItem(ConstService.localStorage.REDIRECT, currentPath);
            await router.push({ name: 'login' });
        }
    }

    private static clearAuthData(): void {
        localStorage.removeItem(ConstService.localStorage.ACCESS_TOKEN);
        localStorage.removeItem(ConstService.localStorage.USER);
    }

    private static updateToken(token: string = ''): void {
        localStorage.setItem(ConstService.localStorage.ACCESS_TOKEN, token);
    }

    private static updateUser(user): void {
        let userJson = '';
        if(user){
            userJson = JSON.stringify(user)
        }
        localStorage.setItem(ConstService.localStorage.USER, userJson);
    }


    public static getToken(): string {
        return localStorage.getItem(ConstService.localStorage.ACCESS_TOKEN) || '';
    }

    public static getUser(): object {
        const userJson = localStorage.getItem(ConstService.localStorage.USER);
        return userJson ? JSON.parse(userJson) : {};
    }

    public static getTokenManager(): TokenManager {
        return this.tokenManager;
    }

    // 로그인 성공 후 토큰을 쿠키에 저장하는 함수
    public static setTokenCookies(accessToken:string, refreshToken:string) {
        const hour = 3600000; // 밀리초 단위로 1시간
        const week = hour * 24 * 14; // 밀리초 단위로 2주일

        document.cookie = `accessToken=${accessToken}; path=/; max-age=${hour}; Secure; SameSite=Strict`;
        document.cookie = `refreshToken=${refreshToken}; path=/; max-age=${week}; Secure; SameSite=Strict`;
    }

    // 쿠키에서 토큰을 추출하는 함수
    public static getTokenFromCookie(name) {
        // Simplify the regex by removing unnecessary escapes
        let matches = document.cookie.match(new RegExp(
            "(?:^|; )" + name.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1') + "=([^;]*)"
        ));
        return matches ? decodeURIComponent(matches[1]) : undefined;
    }

    // 로그아웃시 쿠키에서 토큰 삭제
    public static deleteTokenCookies() {
        document.cookie = "accessToken=; path=/; max-age=-1";
        document.cookie = "refreshToken=; path=/; max-age=-1";
    }
}

export default AuthService;